import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Environment } from 'src/environments/environment';
import {
  ScanComponent,
  ScanResponse,
  ScanRunResponse,
  ScanSubmitPayload,
  ScanSubmitResponse
} from '../models/document-scan.model';
import { InitConfig } from '../models/initConfig.model';
import { FileService } from './file.service';
import { HttpService } from './http.service';

@Injectable({ providedIn: 'root' })
export class DocumentScanService {
  public documentScanInitConfig: InitConfig;

  constructor(
    private httpService: HttpService,
    private fileService: FileService
  ) {}

  public getComponent(componentId: number): Observable<ScanComponent> {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/document-scan/${componentId}`);
  }

  /**
   * Sube desde base64. Evita problemas con Blob/File en Safari/iOS.
   * Usar cuando el picker devuelve file.data.
   */
  public uploadImageFromBase64(
    componentId: number,
    base64Data: string,
    mimeType: string,
    fileName?: string
  ): Observable<ScanRunResponse> {
    const ext = this.mimeToExtension(mimeType);
    const name = (fileName && /\.(jpg|jpeg|png|webp)$/i.test(fileName)) ? fileName : `document.${ext}`;
    const blob = this.fileService.base64ToBlob(base64Data, mimeType);
    const formData = new FormData();
    formData.append('image', blob, name);
    return this.httpService.multipartPost(
      `${Environment.API_BASE_URL_V2}/api/empatica/v1/document-scan/${componentId}/run`,
      formData
    );
  }

  public uploadImage(componentId: number, fileOrBlob: File | Blob, fileName?: string): Observable<ScanRunResponse> {
    const { blob, mimeType } = this.extractBlobAndType(fileOrBlob);
    const ext = this.mimeToExtension(mimeType);
    const name = (fileName && typeof fileName === 'string' && /\.(jpg|jpeg|png|webp)$/i.test(fileName))
      ? fileName
      : `document.${ext}`;
    return from(this.createFormDataBlob(blob, mimeType)).pipe(
      switchMap((freshBlob) => {
        if (!(freshBlob instanceof Blob)) throw new Error('Invalid blob for FormData');
        const formData = new FormData();
        formData.append('image', freshBlob, name);
        return this.httpService.multipartPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/document-scan/${componentId}/run`, formData);
      })
    );
  }

  private mimeToExtension(mime: string): string {
    if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
    if (mime === 'image/png') return 'png';
    if (mime === 'image/webp') return 'webp';
    return 'jpg';
  }

  private async createFormDataBlob(blob: Blob, mimeType: string): Promise<Blob> {
    try {
      let arrayBuffer: ArrayBuffer;
      if (typeof blob.arrayBuffer === 'function') {
        arrayBuffer = await blob.arrayBuffer();
      } else {
        arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsArrayBuffer(blob);
        });
      }
      return new Blob([arrayBuffer], { type: mimeType });
    } catch (err) {
      console.warn('[document-scan] createFormDataBlob arrayBuffer failed, using blob directly:', err);
      try {
        return new Blob([blob], { type: mimeType });
      } catch (fallbackErr) {
        console.error('[document-scan] createFormDataBlob fallback failed:', fallbackErr);
        throw new Error('Failed to prepare file for upload');
      }
    }
  }

  private extractBlobAndType(value: any): { blob: Blob; mimeType: string } {
    const defaultType = 'application/octet-stream';
    const getType = (v: any): string => (typeof v?.type === 'string' ? v.type : v?.type?.type) || '';
    let blobSource: BlobPart | null = null;
    let mimeType = defaultType;
    if (value instanceof Blob || value instanceof File) {
      blobSource = value;
      mimeType = getType(value) || (value.type || '') || defaultType;
    } else if (value && typeof value === 'object') {
      if (value.blob instanceof Blob) {
        blobSource = value.blob;
        mimeType = getType(value) || (value.blob.type || '') || defaultType;
      } else if (Array.isArray(value.name) && value.name.length > 0) {
        const first = value.name[0];
        if (first instanceof Blob || first instanceof ArrayBuffer || ArrayBuffer.isView(first)) {
          blobSource = first as BlobPart;
          const blobType = first instanceof Blob ? (first.type || '') : '';
          mimeType = getType(value) || blobType || defaultType;
        }
      } else if (value.blob) {
        blobSource = value.blob;
        mimeType = getType(value) || defaultType;
      }
    }
    if (!blobSource) throw new Error('Invalid file for upload: value must be a Blob or File instance');
    if (mimeType === defaultType && blobSource instanceof Blob && blobSource.type) {
      mimeType = blobSource.type;
    }
    return { blob: new Blob([blobSource], { type: mimeType }), mimeType };
  }

  public getResponse(responseId: number): Observable<ScanResponse> {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/document-scan/responses/${responseId}`);
  }

  public submitEdits(responseId: number, data: Record<number, any>): Observable<ScanSubmitResponse> {
    const payload: ScanSubmitPayload = { data };
    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/document-scan/responses/${responseId}/submit`, payload);
  }
}
